import React, { useState } from 'react';
import { Upload, FileSpreadsheet, CheckCircle, AlertCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const UploadTerminaliComponent = ({ token }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0]);
      setError('');
      setResult(null);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setError('');
      setResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setError('Seleziona un file prima di caricare');
      return;
    }

    setUploading(true);
    setError('');
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${BACKEND_URL}/api/crm/upload_terminali`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (response.ok) {
        setResult(data);
        setFile(null);
      } else {
        setError(data.detail || 'Errore durante il caricamento');
      }
    } catch (err) {
      setError('Errore di connessione al server');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
      <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
        <FileSpreadsheet className="w-7 h-7 mr-3 text-purple-400" />
        Carica Terminali da Excel
      </h2>

      <p className="text-gray-400 mb-6">
        Carica un file Excel (.xls o .xlsx) con la colonna "Modello" contenente i nomi dei terminali.
        I terminali verranno automaticamente aggiunti al listino TERMINALI.
      </p>

      {/* Drop Zone */}
      <div
        className={`border-2 border-dashed rounded-lg p-8 mb-4 transition-colors ${
          dragActive 
            ? 'border-purple-500 bg-purple-500/10' 
            : 'border-gray-600 hover:border-gray-500'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        <div className="text-center">
          <Upload className="w-12 h-12 text-gray-500 mx-auto mb-4" />
          <p className="text-gray-300 mb-2">
            Trascina qui il file Excel oppure
          </p>
          <label className="inline-block px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg cursor-pointer transition-colors">
            <input
              type="file"
              accept=".xls,.xlsx"
              onChange={handleFileChange}
              className="hidden"
            />
            Seleziona File
          </label>
        </div>
      </div>

      {/* File Selected */}
      {file && (
        <div className="bg-gray-700 rounded-lg p-4 mb-4 flex items-center justify-between">
          <div className="flex items-center">
            <FileSpreadsheet className="w-5 h-5 text-green-400 mr-3" />
            <div>
              <p className="text-white font-medium">{file.name}</p>
              <p className="text-sm text-gray-400">{(file.size / 1024).toFixed(2)} KB</p>
            </div>
          </div>
          <button
            onClick={() => setFile(null)}
            className="text-red-400 hover:text-red-300 text-sm"
          >
            Rimuovi
          </button>
        </div>
      )}

      {/* Upload Button */}
      {file && (
        <button
          onClick={handleUpload}
          disabled={uploading}
          className="w-full px-6 py-3 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 text-white rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2"
        >
          {uploading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              <span>Caricamento in corso...</span>
            </>
          ) : (
            <>
              <Upload size={20} />
              <span>Carica Terminali</span>
            </>
          )}
        </button>
      )}

      {/* Success Message */}
      {result && (
        <div className="mt-4 bg-green-500/10 border border-green-500/50 rounded-lg p-4">
          <div className="flex items-start">
            <CheckCircle className="w-5 h-5 text-green-400 mr-3 mt-0.5" />
            <div>
              <p className="text-green-400 font-semibold mb-2">{result.message}</p>
              <div className="text-sm text-gray-300 space-y-1">
                <p>• Terminali eliminati: {result.terminali_eliminati}</p>
                <p>• Terminali inseriti: {result.terminali_inseriti}</p>
                {result.esempio_terminali && result.esempio_terminali.length > 0 && (
                  <div className="mt-2">
                    <p className="font-medium">Esempi caricati:</p>
                    <ul className="list-disc list-inside ml-2">
                      {result.esempio_terminali.map((t, idx) => (
                        <li key={idx}>{t}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mt-4 bg-red-500/10 border border-red-500/50 rounded-lg p-4">
          <div className="flex items-start">
            <AlertCircle className="w-5 h-5 text-red-400 mr-3 mt-0.5" />
            <p className="text-red-400">{error}</p>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="mt-6 bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
        <p className="text-sm text-blue-300 font-medium mb-2">📋 Istruzioni:</p>
        <ul className="text-sm text-gray-400 space-y-1 list-disc list-inside">
          <li>Il file deve essere in formato Excel (.xls o .xlsx)</li>
          <li>Deve contenere una colonna chiamata "Modello" o "Modello "</li>
          <li>I terminali esistenti verranno sostituiti con quelli del nuovo file</li>
          <li>Solo gli amministratori possono caricare terminali</li>
        </ul>
      </div>
    </div>
  );
};

export default UploadTerminaliComponent;
